import { useState } from "react";
import {
  IconChevronDown as ChevronDown,
  IconMenu2 as Menu,
  IconX as X,
  IconChevronRight as ChevronRight,
  IconUser as UserIcon,
} from "@tabler/icons-react";
import { AnimatePresence, motion } from "motion/react";
import { Logo } from "../Logo";
import { Link, useNavigate } from "react-router-dom";

const NAV_ITEMS = [
  // {
  //   label: "Products",
  //   dropdown: [
  //     {
  //       sectionTitle: "Products",
  //       items: [
  //         { title: "Analytics", desc: "Monitor key metrics", href: "#" },
  //         { title: "Automation", desc: "Build powerful flows", href: "#" },
  //         { title: "Billing", desc: "Manage subscriptions", href: "#" },
  //         { title: "Audiences", desc: "Segment your data", href: "#" },
  //       ],
  //     },
  //     {
  //       sectionTitle: "Resources",
  //       items: [
  //         { title: "Docs", desc: "Developer guides", href: "#" },
  //         { title: "Tutorials", desc: "Step-by-step", href: "#" },
  //         { title: "Templates", desc: "Jumpstart UI", href: "#" },
  //         { title: "Community", desc: "Join discussions", href: "#" },
  //       ],
  //     },
  //   ],
  // },
  // {
  //   label: "Dashboard",
  //   href: "/dashboard",
  // },
];

const tapProps = {
  whileTap: { scale: 0.98 },
  transition: {
    type: "spring",
    stiffness: 500,
    damping: 30,
    mass: 0.6,
  },
};

export function DashboardNavigationBar({ globalActiveWorkFlow = false, refreshing = false, dbActive = true }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState({});
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleDropdownEnter = (label) => {
    setDropdownOpen((prev) => ({ ...prev, [label]: true }));
  };

  const handleDropdownLeave = (label) => {
    setDropdownOpen((prev) => ({ ...prev, [label]: false }));
  };

  const handleSignOut = () => {
    localStorage.clear();
    navigate("/");
  };

  return (
    <header className="bg-card w-full rounded-xl border border-gray-200 max-w-7xl mx-auto my-4">
      <div className="mx-auto max-w-6xl px-4">
        <div className="flex h-16 items-center justify-between gap-4">
          <div className="flex w-full items-center justify-between gap-3 md:w-auto">
            <Link
              to="/dashboard"
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              aria-label="Home"
            >
              <Logo className="h-10 w-auto" />
              <span
                className="text-2xl font-semibold"
                style={{ fontFamily: "'Questrial', sans-serif" }}
              >
                fluvo
              </span>
            </Link>
            <motion.button
              aria-label="Toggle menu"
              className="hover:bg-muted inline-flex size-10 items-center justify-center rounded-md border md:hidden"
              onClick={() => setOpen((s) => !s)}
              whileTap={{ scale: 0.92 }}
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </motion.button>
          </div>

          <nav className="hidden items-center gap-2 md:flex">
            {NAV_ITEMS.map((item) => {
              if (item.dropdown) {
                return (
                  <div key={item.label} className="relative">
                    <motion.button
                      onMouseEnter={() => handleDropdownEnter(item.label)}
                      onMouseLeave={() => handleDropdownLeave(item.label)}
                      className="hover:bg-muted inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm"
                      whileTap={{ scale: 0.97 }}
                      whileHover={{ y: -1 }}
                    >
                      {item.label} <ChevronDown size={16} />
                    </motion.button>
                    <AnimatePresence>
                      {dropdownOpen[item.label] && (
                        <motion.div
                          onMouseEnter={() => handleDropdownEnter(item.label)}
                          onMouseLeave={() => handleDropdownLeave(item.label)}
                          initial={{ opacity: 0, y: -6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -6 }}
                          className="bg-white bg-card absolute top-full left-0 z-20 mt-2 w-[42rem] rounded-xl p-4 ring shadow-sm shadow-black/10 ring-black/10"
                        >
                          <div className="grid grid-cols-2 gap-4">
                            {item.dropdown.map((section, sectionIndex) => (
                              <div
                                key={sectionIndex}
                                className="rounded-lg p-3"
                              >
                                <p className="text-muted-foreground px-1 pb-2 text-xs uppercase">
                                  {section.sectionTitle}
                                </p>
                                <ul className="grid gap-2">
                                  {section.items.map((subItem) => (
                                    <li key={subItem.title}>
                                      <motion.a
                                        href={subItem.href}
                                        className="hover:bg-muted block rounded-md px-2 py-2"
                                        whileHover={{ x: 2 }}
                                        whileTap={{ scale: 0.98 }}
                                      >
                                        <p className="text-sm font-medium">
                                          {subItem.title}
                                        </p>
                                        <p className="text-muted-foreground text-xs">
                                          {subItem.desc}
                                        </p>
                                      </motion.a>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }
              return (
                <motion.a
                  key={item.label}
                  href={item.href}
                  className="hover:bg-muted rounded-md px-3 py-2 text-sm"
                  whileHover={{ y: -1 }}
                  whileTap={{ scale: 0.97 }}
                >
                  {item.label}
                </motion.a>
              );
            })}
          </nav>

          <div className="hidden items-center gap-2 md:flex">
            {refreshing && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                <span className="text-xs text-gray-600">Refreshing...</span>
              </div>
            )}
            {!dbActive && (
              <motion.button
                onClick={() => navigate("/settings#database-connection")}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors"
                whileHover={{ y: -1 }}
                whileTap={{ scale: 0.97 }}
              >
                <svg
                  className="h-4 w-4 text-amber-600"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Setup Database</span>
              </motion.button>
            )}
            <motion.button
              onClick={() => navigate("/settings#workflow-activation")}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                globalActiveWorkFlow
                  ? "bg-green-100 text-green-700 hover:bg-green-200"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              whileHover={{ y: -1 }}
              whileTap={{ scale: 0.97 }}
            >
              <div className={`h-2 w-2 rounded-full ${globalActiveWorkFlow ? 'bg-green-500' : 'bg-gray-400'}`}></div>
              <span>Workflow: {globalActiveWorkFlow ? "Active" : "Inactive"}</span>
            </motion.button>
            <div className="relative">
              <motion.button
                onMouseEnter={() => setUserMenuOpen(true)}
                onMouseLeave={() => setUserMenuOpen(false)}
                className="hover:bg-muted inline-flex items-center justify-center rounded-full border border-gray-200 p-2 transition-colors"
                whileTap={{ scale: 0.97 }}
                whileHover={{ y: -1 }}
                aria-label="User menu"
              >
                <UserIcon size={20} />
              </motion.button>
              <AnimatePresence>
                {userMenuOpen && (
                  <motion.div
                    onMouseEnter={() => setUserMenuOpen(true)}
                    onMouseLeave={() => setUserMenuOpen(false)}
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    className="bg-white bg-card absolute top-full right-0 z-20 mt-2 w-56 rounded-xl p-2 ring shadow-sm shadow-black/10 ring-black/10"
                  >
                    <ul className="grid gap-1">
                      <li>
                        <Link
                          to="/settings#user-details"
                          className="hover:bg-muted block rounded-md px-3 py-2 text-sm font-medium transition-colors"
                        >
                          User Profile
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/settings#business-details"
                          className="hover:bg-muted block rounded-md px-3 py-2 text-sm font-medium transition-colors"
                        >
                          Business Details
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/settings#manage-payment"
                          className="hover:bg-muted block rounded-md px-3 py-2 text-sm font-medium transition-colors"
                        >
                          Manage Payment
                        </Link>
                      </li>
                      <li>
                        <button
                          onClick={handleSignOut}
                          className="hover:bg-muted block w-full text-left rounded-md px-3 py-2 text-sm font-medium text-red-600 transition-colors"
                        >
                          Sign Out
                        </button>
                      </li>
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              className="border-t py-2 md:hidden"
            >
              {NAV_ITEMS.map((item) => {
                if (item.dropdown) {
                  return (
                    <details key={item.label} className="px-3">
                      <summary className="hover:bg-muted flex cursor-pointer list-none items-center justify-between rounded-md px-0 py-2 text-sm">
                        <span>{item.label}</span>
                        <ChevronDown size={16} />
                      </summary>
                      <div className="mt-2 rounded-lg border p-2">
                        {item.dropdown.map((section, sectionIndex) => (
                          <div key={sectionIndex}>
                            {sectionIndex > 0 && (
                              <p className="text-muted-foreground px-1 pt-3 pb-2 text-xs uppercase">
                                {section.sectionTitle}
                              </p>
                            )}
                            {sectionIndex === 0 && (
                              <p className="text-muted-foreground px-1 pb-2 text-xs uppercase">
                                {section.sectionTitle}
                              </p>
                            )}
                            <ul className="grid gap-1">
                              {section.items.map((subItem) => (
                                <li key={subItem.title}>
                                  <motion.a
                                    href={subItem.href}
                                    className="hover:bg-muted flex items-center justify-between rounded-md px-2 py-2 text-sm"
                                    whileHover={{ x: 2 }}
                                    whileTap={{ scale: 0.98 }}
                                  >
                                    <div className="min-w-0">
                                      <p className="font-medium">
                                        {subItem.title}
                                      </p>
                                      <p className="text-muted-foreground text-xs">
                                        {subItem.desc}
                                      </p>
                                    </div>
                                    <ChevronRight
                                      size={16}
                                      className="text-muted-foreground ml-3 shrink-0"
                                    />
                                  </motion.a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </details>
                  );
                }
                return (
                  <motion.a
                    key={item.label}
                    href={item.href}
                    className="hover:bg-muted flex items-center justify-between rounded-md px-3 py-2 text-sm"
                    whileHover={{ x: 2 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span>{item.label}</span>
                    <ChevronRight size={16} className="text-muted-foreground" />
                  </motion.a>
                );
              })}
              <div className="border-t pt-2 mt-2">
                {refreshing && (
                  <div className="flex items-center gap-2 px-3 py-2 text-sm">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                    <span className="text-gray-600">Refreshing...</span>
                  </div>
                )}
                {!dbActive && (
                  <Link
                    to="/settings#database-connection"
                    className="hover:bg-muted flex items-center justify-between rounded-md px-3 py-2 text-sm bg-amber-50"
                  >
                    <div className="flex items-center gap-2">
                      <svg
                        className="h-4 w-4 text-amber-600"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-amber-800 font-medium">Setup Database</span>
                    </div>
                    <ChevronRight size={16} className="text-amber-600" />
                  </Link>
                )}
                <Link
                  to="/settings#workflow-activation"
                  className="hover:bg-muted flex items-center justify-between rounded-md px-3 py-2 text-sm"
                >
                  <div className="flex items-center gap-2">
                    <div className={`h-2 w-2 rounded-full ${globalActiveWorkFlow ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                    <span>Workflow: {globalActiveWorkFlow ? "Active" : "Inactive"}</span>
                  </div>
                  <ChevronRight size={16} className="text-muted-foreground" />
                </Link>
                <Link
                  to="/settings#user-details"
                  className="hover:bg-muted flex items-center justify-between rounded-md px-3 py-2 text-sm"
                >
                  <span>User Profile</span>
                  <ChevronRight size={16} className="text-muted-foreground" />
                </Link>
                <Link
                  to="/settings#business-details"
                  className="hover:bg-muted flex items-center justify-between rounded-md px-3 py-2 text-sm"
                >
                  <span>Business Details</span>
                  <ChevronRight size={16} className="text-muted-foreground" />
                </Link>
                <Link
                  to="/settings#manage-payment"
                  className="hover:bg-muted flex items-center justify-between rounded-md px-3 py-2 text-sm"
                >
                  <span>Manage Payment</span>
                  <ChevronRight size={16} className="text-muted-foreground" />
                </Link>
                <button
                  onClick={handleSignOut}
                  className="hover:bg-muted flex items-center justify-between w-full rounded-md px-3 py-2 text-sm text-red-600"
                >
                  <span>Sign Out</span>
                  <ChevronRight size={16} className="text-red-600" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}
